'use client';

import { useEffect, useState } from 'react';

type ArticleReadingProgressProps = {
  articleElementId: string;
};

export function ArticleReadingProgress({
  articleElementId,
}: ArticleReadingProgressProps) {
  const [progressPercent, setProgressPercent] = useState(0);

  useEffect(() => {
    function updateReadingProgress() {
      const articleElement = document.getElementById(articleElementId);
      if (!articleElement) {
        return;
      }

      const articleTop = articleElement.offsetTop;
      const articleHeight = articleElement.offsetHeight;
      const windowScrollY = window.scrollY;
      const viewportHeight = window.innerHeight;

      if (windowScrollY <= articleTop) {
        setProgressPercent(0);
        return;
      }

      const scrollableDistance = articleHeight - viewportHeight;
      if (scrollableDistance <= 0) {
        setProgressPercent(100);
        return;
      }

      const scrolledPastArticleTop = windowScrollY - articleTop;
      const nextProgressPercent = Math.min(
        Math.max((scrolledPastArticleTop / scrollableDistance) * 100, 0),
        100,
      );
      setProgressPercent(nextProgressPercent);
    }

    updateReadingProgress();
    window.addEventListener('scroll', updateReadingProgress, { passive: true });
    window.addEventListener('resize', updateReadingProgress);

    return () => {
      window.removeEventListener('scroll', updateReadingProgress);
      window.removeEventListener('resize', updateReadingProgress);
    };
  }, [articleElementId]);

  return (
    <div
      className="pointer-events-none fixed top-16 right-0 left-0 z-40 h-1 bg-slate-200/80"
      aria-hidden
    >
      <div
        className="h-full bg-primary transition-[width] duration-100 ease-out"
        style={{ width: `${progressPercent}%` }}
      />
    </div>
  );
}
